import React, { useEffect, useState, useContext } from "react";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import ButtonBase from "@material-ui/core/ButtonBase";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import Avatar from "@material-ui/core/Avatar";
import Switch from "@material-ui/core/Switch";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputBase from "@material-ui/core/InputBase";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import CreateOutlinedIcon from "@material-ui/icons/CreateOutlined";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import BlockIcon from "@material-ui/icons/Block";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import VideocamIcon from "@material-ui/icons/Videocam";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import LinkIcon from "@material-ui/icons/Link";
import MicNoneIcon from "@material-ui/icons/MicNone";
import ChatOutlinedIcon from "@material-ui/icons/ChatOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import formatSerializedId from "../../utils/formatSerializedId";
import { i18n } from "../../translate/i18n";
import MarkdownWrapper from "../MarkdownWrapper";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import { requestJumpToMessage } from "../MessagesList/jumpToMessage";
import { normalizeParticipant } from "../MessagesList/mentions";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { TagsKanbanContainer } from "../TagsKanbanContainer";
import { TagsContainer } from "../TagsContainer";
import NewTicketModal from "../NewTicketModal";
import ShowTicketOpenModal from "../ShowTicketOpenModal";
import ContactErpPanel from "../ContactErpPanel";
import WebchatVisitorInfo from "../WebchatVisitorInfo";

const drawerWidth = 312;

// Painel "Dados do contato" no padrão do WhatsApp Web: cabeçalho na mesma faixa da conversa,
// blocos planos separados por uma faixa cinza e ações no fim. Paleta fixa (não segue a marca),
// a mesma família de cinzas da conversa e da lista.
const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	const wa = isDark
		? {
			band: "#0c1317",
			card: "#111b21",
			header: "#202c33",
			field: "#202c33",
			hover: "#202c33",
			text: "#e9edef",
			secondary: "#8696a0",
			line: "rgba(134,150,160,0.15)",
			green: "#00a884",
			mark: "rgba(0,168,132,0.35)",
		}
		: {
			band: "#f0f2f5",
			card: "#ffffff",
			header: "#f0f2f5",
			field: "#f0f2f5",
			hover: "#f5f6f6",
			text: "#111b21",
			secondary: "#667781",
			line: "#e9edef",
			green: "#008069",
			mark: "#d9fdd3",
		};
	const danger = isDark ? "#f15c6d" : "#ea0038";
	const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

	return {
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			zIndex: "auto",
		},
		drawerPaper: {
			width: drawerWidth,
			display: "flex",
			flexDirection: "column",
			border: 0,
			borderLeft: `1px solid ${wa.line}`,
			borderRadius: 0,
			height: "100%",
			overflow: "hidden",
			backgroundColor: wa.band,
			color: wa.text,
			[theme.breakpoints.down("sm")]: {
				width: "100%",
				borderLeft: 0,
			},
		},
		header: {
			flex: "none",
			display: "flex",
			alignItems: "center",
			gap: 14,
			height: 59,
			padding: "0 14px 0 10px",
			backgroundColor: wa.header,
		},
		headerButton: {
			width: 38,
			height: 38,
			padding: 0,
			borderRadius: "50%",
			color: wa.secondary,
			"&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,20,26,0.06)" },
			"& .MuiSvgIcon-root": { fontSize: 22 },
		},
		headerTitle: {
			flex: 1,
			minWidth: 0,
			margin: 0,
			fontSize: 16,
			fontWeight: 500,
			color: wa.text,
			...ellipsis,
		},
		body: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			overflowX: "hidden",
			display: "flex",
			flexDirection: "column",
			gap: 8,
			paddingBottom: 16,
			...theme.scrollbarStyles,
		},
		// bloco plano; a faixa cinza entre eles é o próprio fundo do painel
		block: {
			flex: "none",
			backgroundColor: wa.card,
			padding: "14px 18px",
			minWidth: 0,
		},
		blockFlush: {
			padding: "6px 0",
		},
		blockTitle: {
			margin: "0 0 10px",
			fontSize: 13.5,
			fontWeight: 400,
			color: wa.secondary,
		},
		blockTitleRow: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 8,
			marginBottom: 6,
			"& $blockTitle": { margin: 0 },
		},
		profile: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			textAlign: "center",
			padding: "24px 18px 16px",
		},
		avatar: {
			width: 132,
			height: 132,
			marginBottom: 14,
			fontSize: 48,
			fontWeight: 500,
			cursor: "pointer",
		},
		avatarStatic: { cursor: "default" },
		name: {
			maxWidth: "100%",
			fontSize: 20,
			lineHeight: "26px",
			fontWeight: 500,
			color: wa.text,
			wordBreak: "break-word",
		},
		number: {
			marginTop: 2,
			fontSize: 15,
			lineHeight: "22px",
			color: wa.secondary,
		},
		numberWarn: { color: danger },
		warnLine: {
			display: "block",
			fontSize: 11.5,
			color: danger,
		},
		subtle: {
			marginTop: 2,
			fontSize: 12.5,
			color: wa.secondary,
			wordBreak: "break-all",
		},
		email: {
			marginTop: 2,
			fontSize: 13.5,
			wordBreak: "break-all",
			"& a": { color: wa.green },
		},
		quickActions: {
			display: "flex",
			justifyContent: "center",
			gap: 10,
			marginTop: 16,
			width: "100%",
		},
		quickAction: {
			flex: "0 1 104px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 4,
			padding: "10px 8px 8px",
			border: `1px solid ${wa.line}`,
			borderRadius: 12,
			fontFamily: "inherit",
			fontSize: 13,
			color: wa.text,
			"& .MuiSvgIcon-root": { fontSize: 21, color: wa.green },
			"&:hover": { backgroundColor: wa.hover },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: 1 },
		},
		// linha de dado: ícone, rótulo pequeno e valor
		infoRow: {
			display: "flex",
			alignItems: "center",
			gap: 16,
			minHeight: 44,
			"& > .MuiSvgIcon-root": { flex: "none", fontSize: 21, color: wa.secondary },
		},
		infoText: {
			flex: 1,
			minWidth: 0,
			"& span": { display: "block", fontSize: 12, color: wa.secondary },
			"& strong": { display: "block", fontSize: 14.5, fontWeight: 400, color: wa.text, ...ellipsis },
		},
		switchRow: {
			display: "flex",
			alignItems: "center",
			gap: 16,
			minHeight: 44,
			cursor: "pointer",
			"& > .MuiSvgIcon-root": { flex: "none", fontSize: 21, color: wa.secondary },
			"& > span:first-of-type": { flex: 1, minWidth: 0, fontSize: 14.5, color: wa.text },
		},
		// linha clicável de bloco (abre uma subtela)
		navRow: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: 16,
			minHeight: 52,
			padding: "8px 12px 8px 18px",
			textAlign: "left",
			fontFamily: "inherit",
			color: wa.text,
			"&:hover": { backgroundColor: wa.hover },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: -2 },
			"& > .MuiSvgIcon-root": { flex: "none", fontSize: 21, color: wa.secondary },
		},
		navText: {
			flex: 1,
			minWidth: 0,
			"& strong": { display: "block", fontSize: 14.5, fontWeight: 400, ...ellipsis },
			"& span": { display: "block", fontSize: 12.5, color: wa.secondary, ...ellipsis },
		},
		navCount: {
			flex: "none",
			display: "inline-flex",
			alignItems: "center",
			gap: 2,
			fontSize: 13.5,
			color: wa.secondary,
			"& .MuiSvgIcon-root": { fontSize: 20 },
		},
		navRowTitle: {
			minHeight: 40,
			"& $navText strong": { fontSize: 13.5, color: wa.secondary },
		},
		thumbStrip: {
			display: "grid",
			gridTemplateColumns: "repeat(3, 1fr)",
			gap: 6,
			padding: "2px 18px 10px",
		},
		thumb: {
			position: "relative",
			aspectRatio: "1 / 1",
			width: "100%",
			padding: 0,
			border: 0,
			borderRadius: 8,
			overflow: "hidden",
			cursor: "pointer",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 4,
			backgroundColor: wa.field,
			color: wa.secondary,
			fontFamily: "inherit",
			fontSize: 11,
			"& img": { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" },
			"& .MuiSvgIcon-root": { fontSize: 28 },
			"&:hover": { filter: "brightness(0.94)" },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: 1 },
		},
		mediaGrid: {
			display: "grid",
			gridTemplateColumns: "repeat(3, 1fr)",
			gap: 6,
			padding: 12,
		},
		tags: {
			display: "flex",
			flexDirection: "column",
			gap: 8,
			minWidth: 0,
		},
		extraItem: {
			padding: "8px 0",
			minWidth: 0,
			"& + &": { borderTop: `1px solid ${wa.line}` },
			"& > span": { display: "block", fontSize: 12, color: wa.secondary },
			"& > div": { fontSize: 14.5, color: wa.text, wordBreak: "break-word" },
		},
		emptyText: {
			margin: 0,
			fontSize: 13.5,
			color: wa.secondary,
		},
		// ações do fim do painel
		actionRow: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: 16,
			minHeight: 52,
			padding: "8px 18px",
			textAlign: "left",
			fontFamily: "inherit",
			fontSize: 14.5,
			color: wa.text,
			"& .MuiSvgIcon-root": { flex: "none", fontSize: 21, color: wa.secondary },
			"&:hover": { backgroundColor: wa.hover },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: -2 },
			"&.Mui-disabled": { opacity: 0.5 },
		},
		actionDanger: {
			color: danger,
			"& .MuiSvgIcon-root": { color: danger },
		},
		actionPositive: {
			color: wa.green,
			"& .MuiSvgIcon-root": { color: wa.green },
		},
		// participantes do grupo
		participant: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: 12,
			minHeight: 60,
			padding: "8px 14px 8px 18px",
			textAlign: "left",
			fontFamily: "inherit",
			color: wa.text,
			"&:hover": { backgroundColor: wa.hover },
			"&:hover $participantChat": { opacity: 1 },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: -2 },
		},
		participantAvatar: {
			flex: "none",
			width: 40,
			height: 40,
		},
		participantText: {
			flex: 1,
			minWidth: 0,
			"& strong": { display: "block", fontSize: 14.5, fontWeight: 400, ...ellipsis },
			"& span": { display: "block", fontSize: 12.5, color: wa.secondary, ...ellipsis },
		},
		participantChat: {
			flex: "none",
			fontSize: 19,
			color: wa.secondary,
			opacity: 0.55,
			transition: "opacity 120ms ease",
		},
		roleBadge: {
			flex: "none",
			padding: "2px 6px",
			borderRadius: 4,
			fontSize: 11,
			lineHeight: "15px",
			color: wa.green,
			backgroundColor: isDark ? "rgba(0,168,132,0.16)" : "#e7fce3",
		},
		smallButton: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			padding: "4px 8px",
			borderRadius: 14,
			fontFamily: "inherit",
			fontSize: 12.5,
			fontWeight: 500,
			color: wa.green,
			"& .MuiSvgIcon-root": { fontSize: 16 },
			"&:hover": { backgroundColor: wa.hover },
			"&.Mui-disabled": { opacity: 0.45 },
		},
		loading: {
			display: "flex",
			justifyContent: "center",
			padding: 20,
			color: wa.secondary,
		},
		// subtelas (pesquisa, mídias, ERP)
		subBody: {
			flex: 1,
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			backgroundColor: wa.card,
		},
		subScroll: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			overflowX: "hidden",
			...theme.scrollbarStyles,
		},
		erpBody: {
			padding: "4px 12px 16px",
		},
		searchBar: {
			flex: "none",
			padding: "8px 12px",
			borderBottom: `1px solid ${wa.line}`,
		},
		searchField: {
			width: "100%",
			height: 36,
			padding: "0 6px 0 12px",
			gap: 8,
			borderRadius: 8,
			backgroundColor: wa.field,
			color: wa.text,
			fontSize: 14,
			"& > .MuiSvgIcon-root": { fontSize: 18, color: wa.secondary },
			"& input::placeholder": { color: wa.secondary, opacity: 1 },
			"&.Mui-focused": { boxShadow: `0 0 0 1.5px ${wa.green} inset` },
		},
		searchResult: {
			width: "100%",
			display: "block",
			padding: "10px 18px",
			textAlign: "left",
			fontFamily: "inherit",
			borderBottom: `1px solid ${wa.line}`,
			"&:hover": { backgroundColor: wa.hover },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: -2 },
		},
		searchResultDate: {
			display: "block",
			fontSize: 12,
			color: wa.secondary,
			marginBottom: 2,
		},
		searchResultText: {
			display: "block",
			fontSize: 14,
			lineHeight: "19px",
			color: wa.text,
			wordBreak: "break-word",
			"& mark": {
				backgroundColor: wa.mark,
				color: "inherit",
				padding: "0 1px",
				borderRadius: 2,
			},
		},
		emptyState: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 8,
			padding: "36px 24px",
			textAlign: "center",
			fontSize: 13.5,
			color: wa.secondary,
			"& .MuiSvgIcon-root": { fontSize: 34, opacity: 0.6 },
		},
		tabs: {
			flex: "none",
			minHeight: 42,
			borderBottom: `1px solid ${wa.line}`,
			"& .MuiTabs-indicator": { backgroundColor: wa.green, height: 3 },
			"& .MuiTabs-scrollButtons": { width: 24, color: wa.secondary },
		},
		tab: {
			minWidth: 0,
			minHeight: 42,
			padding: "6px 12px",
			fontSize: 13,
			fontWeight: 500,
			color: wa.secondary,
			borderRadius: 0,
			"&.Mui-selected": { color: wa.green, backgroundColor: "transparent" },
		},
		listRow: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: 14,
			minHeight: 56,
			padding: "8px 18px",
			textAlign: "left",
			fontFamily: "inherit",
			color: wa.text,
			borderBottom: `1px solid ${wa.line}`,
			"& > .MuiSvgIcon-root": { flex: "none", fontSize: 24, color: wa.secondary },
			"&:hover": { backgroundColor: wa.hover },
			"&:focus-visible": { outline: `2px solid ${wa.green}`, outlineOffset: -2 },
		},
		// foto do contato ampliada
		imageDialogPaper: {
			width: "auto",
			maxWidth: "min(92vw, 560px)",
			margin: 16,
			padding: 0,
			border: 0,
			overflow: "hidden",
			backgroundColor: "transparent",
			boxShadow: "none",
		},
		expandedImage: {
			display: "block",
			maxWidth: "100%",
			maxHeight: "86vh",
			borderRadius: 8,
		},
	};
});

// Escapa o termo antes de virar expressão regular: o que o atendente digita é texto, não padrão.
const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Destaca o termo como elementos React (sem HTML cru): o corpo da mensagem vem de terceiros.
const highlightTerm = (text, term) => {
	const value = String(text || "");
	const needle = String(term || "").trim();
	if (!needle) return value;
	const parts = value.split(new RegExp(`(${escapeRegExp(needle)})`, "gi"));
	return parts.map((part, index) =>
		index % 2 === 1 ? <mark key={index}>{part}</mark> : part
	);
};

const withBackendUrl = (item) => ({
	...item,
	mediaUrl: item.mediaUrl && !item.mediaUrl.startsWith("http")
		? `${getBackendUrl()}${item.mediaUrl}`
		: item.mediaUrl,
});

const formatDay = (iso) => new Date(iso).toLocaleDateString("pt-BR");

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const history = useHistory();

	const [modalOpen, setModalOpen] = useState(false);
	const [, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	// "main" (dados do contato) ou uma subtela: "search", "media", "erp"
	const [view, setView] = useState("main");
	const [tabValue, setTabValue] = useState(0);
	const [mediaData, setMediaData] = useState({ images: [], videos: [], audios: [], documents: [], links: [] });
	const [loadingMedia, setLoadingMedia] = useState(false);
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const [participantInitialContact, setParticipantInitialContact] = useState(undefined);
	const [openTicketAlert, setOpenTicketAlert] = useState(false);
	const [openTicketInfo, setOpenTicketInfo] = useState({ user: "", queue: "" });
	const contactInitial = Array.from(String(contact?.name || "?").trim())[0] || "?";

	const [participants, setParticipants] = useState([]);
	const [loadingParticipants, setLoadingParticipants] = useState(false);

	// Estados para pesquisa
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [showSearchResults, setShowSearchResults] = useState(false);
	const [searchTimeout, setSearchTimeout] = useState(null);

	// Função para buscar participantes do grupo
	const fetchGroupParticipants = async () => {
		if (!contact.isGroup) return;

		setLoadingParticipants(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/participants`);
			setParticipants(data);
		} catch (err) {
			console.error("Erro ao buscar participantes do grupo:", err);
			toastError("Erro ao carregar participantes do grupo");
			setParticipants([]);
		} finally {
			setLoadingParticipants(false);
		}
	};

	useEffect(() => {
		async function fetchData() {
			const lgpdHideNumber = await get({
				"column": "lgpdHideNumber"
			});

			if (lgpdHideNumber === "enabled") setHideNum(true);
		}
		fetchData();
	}, []);

	useEffect(() => {
		setAcceptAudio(contact.acceptAudioMessage);
		setOpenForm(false);
		setParticipants([]); // Limpar participantes

		if (open && contact.id) {
			fetchMediaData();
			// Buscar participantes apenas se for um grupo
			if (contact.isGroup) {
				fetchGroupParticipants();
			}
		}
	}, [open, contact]);

	// Trocar de contato (ou reabrir o painel) volta para a tela principal e limpa a pesquisa.
	// Fica separado do efeito acima para uma simples atualização do contato não tirar o
	// atendente da subtela em que está.
	useEffect(() => {
		setView("main");
		setTabValue(0);
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
	}, [open, contact.id]);

	// Função para abrir modal da imagem
	const handleImageClick = () => {
		if (contact?.urlPicture) {
			setImageModalOpen(true);
		}
	};

	// Função para fechar modal da imagem
	const handleImageModalClose = () => {
		setImageModalOpen(false);
	};

	// Função para buscar mensagens
	const searchMessages = async (searchParam) => {
		if (!searchParam || searchParam.trim().length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			return;
		}

		setSearchLoading(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/messages/search`, {
				params: { searchParam: searchParam.trim() }
			});

			setSearchResults(data.messages || []);
			setShowSearchResults(true);
		} catch (err) {
			console.error("Erro ao buscar mensagens:", err);
			toastError(err);
			setSearchResults([]);
		} finally {
			setSearchLoading(false);
		}
	};

	// Handler para mudança no campo de pesquisa com debounce
	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);

		// Clear timeout anterior
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Definir novo timeout para debounce
		const newTimeout = setTimeout(() => {
			searchMessages(value);
		}, 500);

		setSearchTimeout(newTimeout);
	};

	// Limpar pesquisa
	const clearSearch = () => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
	};

	// Leva até a mensagem na conversa; se ela ainda não está carregada, a lista de mensagens
	// busca as páginas antigas até encontrá-la (e avisa se não for desta conversa).
	const handleGoToMessage = (message) => {
		requestJumpToMessage({ id: message.id, createdAt: message.createdAt });
		if (isMobile) handleDrawerClose();
	};

	const fetchMediaData = async () => {
		setLoadingMedia(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/media`);
			// Garantir que as URLs das mídias estejam corretas
			setMediaData({
				images: data.images.map(withBackendUrl),
				videos: data.videos.map(withBackendUrl),
				audios: data.audios.map(withBackendUrl),
				documents: data.documents.map(withBackendUrl),
				links: data.links
			});
		} catch (err) {
			console.error('Erro ao buscar mídias do contato:', {
				status: err?.response?.status,
				url: err?.config?.url,
				message: err?.message
			});
			toastError(err);
			setMediaData({
				images: [],
				videos: [],
				audios: [],
				documents: [],
				links: []
			});
		} finally {
			setLoadingMedia(false);
		}
	};

	// Abrir modal de conversa com participante
	const handleOpenChatWithParticipant = async (participant) => {
		try {
			// Buscar contato pelo número; se não existir, criar
			const profile = await api.get(`/contacts/profile/${participant.number}`);
			let contactId = profile?.data?.contactId || 0;
			if (!contactId || contactId === 0) {
				const createResp = await api.post('/contacts', { name: participant.name || participant.number, number: participant.number });
				contactId = createResp?.data?.id;
			}

			if (contactId) {
				// Verificar se já existe ticket aberto para este contato
				try {
					// Buscar todos os tickets abertos (open, pending, chatbot)
					const ticketsResponse = await api.get('/tickets', {
						params: {
							pageNumber: "1"
						}
					});

					// Procurar ticket existente para este contato
					const existingTicket = ticketsResponse.data?.tickets?.find(
						ticket => ticket.contactId === contactId && 
							["open", "pending", "chatbot"].includes(ticket.status)
					);

					if (existingTicket) {
						// Ticket já existe - verificar se pertence a outro usuário
						if (existingTicket.userId !== user.id) {
							setOpenTicketInfo({
								user: existingTicket.user?.name || "Outro usuário",
								queue: existingTicket.queue?.name || "Sem fila"
							});
							setOpenTicketAlert(true);
							return;
						} else {
							// Ticket existe e pertence ao próprio usuário
							// Navegar para o ticket existente
							handleTicketCreated(existingTicket);
							return;
						}
					}
				} catch (checkError) {
					console.error('Erro ao verificar tickets existentes:', checkError);
					toastError('Erro ao verificar tickets existentes');
					return;
				}

				// Se chegou aqui, pode abrir o modal para criar ticket
				setParticipantInitialContact({ id: contactId, name: participant.name, number: participant.number });
				setNewTicketModalOpen(true);
			}
		} catch (err) {
			console.error('Erro ao abrir conversa com participante:', err);
			toastError('Não foi possível iniciar a conversa com este participante');
		}
	};

	// Handler para quando o ticket for criado
  const handleTicketCreated = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket && (ticket.uuid || ticket.id)) {
      const target = ticket.uuid ? `/tickets/${ticket.uuid}` : `/tickets/${ticket.id}`;
      history.push(target);
    }
  };

	// Função para exportar participantes para Excel
	const handleExportParticipants = () => {
		if (participants.length === 0) {
			toast.error("Não há participantes para exportar");
			return;
		}

		try {
			// Preparar dados para exportação
			const exportData = participants.map((participant, index) => ({
				"#": index + 1,
				"Nome": participant.name || "Sem nome",
				"Número": participant.number ? formatSerializedId(participant.number) : "",
				"Role": participant.isSuperAdmin 
					? "Super Admin" 
					: participant.isAdmin 
					? "Admin" 
					: "Participante"
			}));

			// Criar workbook e worksheet
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(exportData);

			// Ajustar largura das colunas
			const colWidths = [
				{ wch: 5 },  // #
				{ wch: 30 }, // Nome
				{ wch: 20 }, // Número
				{ wch: 15 }  // Role
			];
			ws['!cols'] = colWidths;

			// Adicionar worksheet ao workbook
			XLSX.utils.book_append_sheet(wb, ws, "Participantes");

			// Gerar nome do arquivo com o nome do grupo e data
			const groupName = contact.name || "Grupo";
			const fileName = `Participantes_${groupName}_${new Date().toISOString().split('T')[0]}.xlsx`;

			// Fazer download do arquivo
			XLSX.writeFile(wb, fileName);

			toast.success(`Arquivo "${fileName}" exportado com sucesso!`);
		} catch (err) {
			console.error("Erro ao exportar participantes:", err);
			toastError("Erro ao exportar participantes");
		}
	};

	const handleContactToggleAcceptAudio = async () => {
		try {
			const contact = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
			setAcceptAudio(contact.data.acceptAudioMessage);
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success("Contato bloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(true);
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success("Contato desbloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
	};

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	const openEditContact = () => setModalOpen(!openForm);

	const hasLidAsNumber = Boolean(contact.number && contact.number.includes('@'));
	// o contato que vem junto do ticket não traz `channel`; o ticket traz (e webchat só tem contato de webchat)
	const isWebchatContact = contact?.channel === "webchat" || ticket?.channel === "webchat";
	const displayNumber = isWebchatContact ? "Webchat" : hideNum && user.profile === "user"
		? formatSerializedId(contact.number).slice(0, -6) + "**-**" + contact.number.slice(-2)
		: formatSerializedId(contact.number);

	const mediaTotal = mediaData.images.length + mediaData.videos.length + mediaData.audios.length
		+ mediaData.documents.length + mediaData.links.length;
	// faixa de prévia: as últimas imagens e, se sobrar espaço, vídeos
	const previewItems = [
		...mediaData.images.map(item => ({ ...item, kind: "image" })),
		...mediaData.videos.map(item => ({ ...item, kind: "video" })),
	].slice(0, 3);

	const renderHeader = (title, onBack) => (
		<div className={classes.header}>
			<IconButton
				className={classes.headerButton}
				onClick={onBack || handleDrawerClose}
				aria-label={onBack ? "Voltar" : "Fechar dados do contato"}
			>
				{onBack ? <ArrowBackIcon /> : <CloseIcon />}
			</IconButton>
			<h2 className={classes.headerTitle}>{title}</h2>
		</div>
	);

	const renderThumb = (item, index, kind) => (
		<button
			type="button"
			key={`${kind}-${index}`}
			className={classes.thumb}
			onClick={() => window.open(item.mediaUrl, '_blank')}
			aria-label={kind === "image" ? `Abrir imagem ${index + 1}` : kind === "video" ? `Abrir vídeo ${index + 1}` : `Abrir áudio ${index + 1}`}
		>
			{kind === "image" && <img src={item.mediaUrl} alt="" />}
			{kind === "video" && (<><VideocamIcon /><span>{formatDay(item.createdAt)}</span></>)}
			{kind === "audio" && (<><AudiotrackIcon /><span>{formatDay(item.createdAt)}</span></>)}
		</button>
	);

	const renderEmpty = (icon, text) => (
		<div className={classes.emptyState}>
			{icon}
			<span>{text}</span>
		</div>
	);

	const renderMediaTab = () => {
		if (loadingMedia) {
			return (
				<div className={classes.loading}>
					<CircularProgress size={26} color="inherit" />
				</div>
			);
		}
		if (tabValue === 0) {
			return mediaData.images.length === 0
				? renderEmpty(<InsertDriveFileOutlinedIcon />, "Nenhuma imagem encontrada")
				: <div className={classes.mediaGrid}>{mediaData.images.map((item, index) => renderThumb(item, index, "image"))}</div>;
		}
		if (tabValue === 1) {
			return mediaData.videos.length === 0
				? renderEmpty(<VideocamIcon />, "Nenhum vídeo encontrado")
				: <div className={classes.mediaGrid}>{mediaData.videos.map((item, index) => renderThumb(item, index, "video"))}</div>;
		}
		if (tabValue === 2) {
			return mediaData.audios.length === 0
				? renderEmpty(<AudiotrackIcon />, "Nenhum áudio encontrado")
				: <div className={classes.mediaGrid}>{mediaData.audios.map((item, index) => renderThumb(item, index, "audio"))}</div>;
		}
		if (tabValue === 3) {
			return mediaData.documents.length === 0
				? renderEmpty(<InsertDriveFileOutlinedIcon />, "Nenhum documento encontrado")
				: mediaData.documents.map((doc, index) => (
					<ButtonBase
						key={index}
						className={classes.listRow}
						onClick={() => window.open(doc.mediaUrl, '_blank')}
					>
						<InsertDriveFileOutlinedIcon />
						<span className={classes.navText}>
							<strong>{doc.body || `Documento ${index + 1}`}</strong>
							<span>{formatDay(doc.createdAt)}</span>
						</span>
					</ButtonBase>
				));
		}
		return mediaData.links.length === 0
			? renderEmpty(<LinkIcon />, "Nenhum link encontrado")
			: mediaData.links.map((link, index) => (
				<ButtonBase
					key={index}
					className={classes.listRow}
					onClick={() => window.open(link.url, '_blank')}
				>
					<LinkIcon />
					<span className={classes.navText}>
						<strong>{link.title || link.url}</strong>
						<span>{formatDay(link.createdAt)}</span>
					</span>
				</ButtonBase>
			));
	};

	const renderSearchResults = () => {
		if (searchLoading) {
			return (
				<div className={classes.loading}>
					<CircularProgress size={22} color="inherit" />
				</div>
			);
		}
		if (!showSearchResults) {
			return renderEmpty(<SearchIcon />, `Pesquise mensagens trocadas com ${contact.name || "este contato"}.`);
		}
		if (searchResults.length === 0) {
			return renderEmpty(<SearchIcon />, "Nenhuma mensagem encontrada");
		}
		return searchResults.map((message) => {
			const body = String(message.body || "");
			const excerpt = body.substring(0, 100) + (body.length > 100 ? "..." : "");
			return (
				<ButtonBase
					key={message.id}
					className={classes.searchResult}
					onClick={() => handleGoToMessage(message)}
				>
					<span className={classes.searchResultDate}>
						{new Date(message.createdAt).toLocaleString('pt-BR')}
						{message.fromMe ? " (Você)" : ` (${contact.name})`}
					</span>
					<span className={classes.searchResultText}>
						{highlightTerm(excerpt, searchTerm)}
					</span>
				</ButtonBase>
			);
		});
	};

	const renderParticipants = () => {
		if (loadingParticipants) {
			return (
				<div className={classes.loading}>
					<CircularProgress size={26} color="inherit" />
				</div>
			);
		}
		if (participants.length === 0) {
			return <p className={classes.emptyText} style={{ padding: "4px 18px 10px" }}>Nenhum participante encontrado</p>;
		}
		return participants.map((participant) => {
			// O servidor pode devolver participante sem nome e/ou sem número (só o LID, o
			// identificador interno do WhatsApp). Sem número não há como abrir conversa.
			const known = normalizeParticipant(participant) || { name: "", number: "", lid: "" };
			const phone = known.number ? formatSerializedId(known.number) : "";
			const title = known.name || phone || "Participante";
			const subtitle = known.name ? phone : phone ? "" : known.lid ? `ID ${known.lid}` : "";
			return (
				<ButtonBase
					key={participant.id}
					className={classes.participant}
					onClick={() => handleOpenChatWithParticipant({ ...participant, name: known.name, number: known.number })}
					disabled={!known.number}
					title={known.number ? "Conversar" : "Número não informado pelo WhatsApp"}
				>
					<Avatar
						src={participant.profilePicUrl}
						alt={title}
						className={classes.participantAvatar}
					>
						{known.name ? known.name.charAt(0).toUpperCase() : null}
					</Avatar>
					<span className={classes.participantText}>
						<strong>{title}</strong>
						{subtitle && <span>{subtitle}</span>}
					</span>
					{participant.isSuperAdmin && <span className={classes.roleBadge}>Super Admin</span>}
					{participant.isAdmin && !participant.isSuperAdmin && <span className={classes.roleBadge}>Admin</span>}
					{known.number && <ChatOutlinedIcon className={classes.participantChat} />}
				</ButtonBase>
			);
		});
	};

	const renderMain = () => (
		<>
			{renderHeader(i18n.t("contactDrawer.header"))}
			<div className={classes.body}>
				{/* Perfil */}
				<section className={clsx(classes.block, classes.profile)}>
					<Avatar
						src={contact?.urlPicture}
						alt={contact.name}
						className={clsx(classes.avatar, !contact?.urlPicture && classes.avatarStatic)}
						onClick={handleImageClick}
					>
						{contactInitial.toUpperCase()}
					</Avatar>
					<div className={classes.name}>{contact.name}</div>
					<div className={clsx(classes.number, hasLidAsNumber && classes.numberWarn)}>
						{hasLidAsNumber && (
							<span className={classes.warnLine}>
								⚠️ Possível erro: Número parece ser LID
							</span>
						)}
						{displayNumber}
					</div>
					{isWebchatContact && <WebchatVisitorInfo contactId={contact.id} hideNumber={hideNum && user?.profile === "user"} />}
					{/* LID aparece apenas quando há um valor útil. */}
					{contact.lid && <div className={classes.subtle}>LID: {contact.lid}</div>}
					{contact.email && (
						<div className={classes.email}>
							<Link href={`mailto:${contact.email}`}>{contact.email}</Link>
						</div>
					)}

					<div className={classes.quickActions}>
						<ButtonBase className={classes.quickAction} onClick={() => setView("search")}>
							<SearchIcon />
							Pesquisar
						</ButtonBase>
						<ButtonBase className={classes.quickAction} onClick={openEditContact}>
							<CreateOutlinedIcon />
							Editar
						</ButtonBase>
					</div>

					{(contact.id && openForm) && <ContactForm initialContact={contact} onCancel={() => setOpenForm(false)} />}
				</section>

				{/* Atendimento */}
				<section className={classes.block}>
					<h3 className={classes.blockTitle}>Atendimento</h3>
					<div className={classes.infoRow}>
						<PersonOutlineIcon />
						<div className={classes.infoText}>
							<span>Responsável</span>
							<strong>{ticket?.user?.name || "Não atribuído"}</strong>
						</div>
					</div>
					<div className={classes.infoRow}>
						<ForumOutlinedIcon />
						<div className={classes.infoText}>
							<span>Fila</span>
							<strong>{ticket?.queue?.name || "Sem fila"}</strong>
						</div>
					</div>
					<label className={classes.switchRow}>
						<MicNoneIcon />
						<span>{i18n.t("ticketOptionsMenu.acceptAudioMessage")}</span>
						<Switch
							size="small"
							checked={Boolean(acceptAudioMessage)}
							onChange={() => handleContactToggleAcceptAudio()}
							name="disableBot"
							color="primary"
						/>
					</label>
				</section>

				{/* Mídia, links e docs */}
				<section className={clsx(classes.block, classes.blockFlush)}>
					<ButtonBase
						className={clsx(classes.navRow, classes.navRowTitle)}
						onClick={() => setView("media")}
						aria-label="Ver todas as mídias, links e documentos"
					>
						<span className={classes.navText}>
							<strong>Mídia, links e docs</strong>
						</span>
						<span className={classes.navCount}>
							{loadingMedia ? <CircularProgress size={14} color="inherit" /> : mediaTotal}
							<ChevronRightIcon />
						</span>
					</ButtonBase>
					{previewItems.length > 0 && (
						<div className={classes.thumbStrip}>
							{previewItems.map((item, index) => renderThumb(item, index, item.kind))}
						</div>
					)}
				</section>

				{/* Etiquetas */}
				<section className={classes.block}>
					<h3 className={classes.blockTitle}>Etiquetas</h3>
					<div className={classes.tags}>
						<TagsContainer contact={contact} />
						<TagsKanbanContainer ticket={ticket} />
					</div>
				</section>

				{/* Observações */}
				<section className={classes.block}>
					<h3 className={classes.blockTitle}>
						{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
					</h3>
					<ContactNotes ticket={ticket} />
				</section>

				{/* Outras informações */}
				<section className={classes.block}>
					<h3 className={classes.blockTitle}>{i18n.t("contactDrawer.extraInfo")}</h3>
					{contact?.extraInfo?.length > 0 ? (
						contact.extraInfo.map(info => (
							<div key={info.id} className={classes.extraItem}>
								<span>{info.name}</span>
								<div>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</div>
							</div>
						))
					) : (
						<p className={classes.emptyText}>Nenhuma informação adicional.</p>
					)}
				</section>

				{/* ERP (AtendeISP) */}
				<section className={clsx(classes.block, classes.blockFlush)}>
					<ButtonBase className={classes.navRow} onClick={() => setView("erp")}>
						<BusinessIcon />
						<span className={classes.navText}>
							<strong>ERP</strong>
							<span>Contratos, faturas e conexão</span>
						</span>
						<span className={classes.navCount}>
							<ChevronRightIcon />
						</span>
					</ButtonBase>
				</section>

				{/* Participantes do grupo */}
				{contact.isGroup && (
					<section className={clsx(classes.block, classes.blockFlush)}>
						<div className={classes.blockTitleRow} style={{ padding: "8px 12px 0 18px" }}>
							<h3 className={classes.blockTitle}>
								{participants.length} {participants.length === 1 ? "participante" : "participantes"}
							</h3>
							<ButtonBase
								className={classes.smallButton}
								onClick={handleExportParticipants}
								disabled={participants.length === 0}
							>
								<GetAppIcon />
								Exportar
							</ButtonBase>
						</div>
						{renderParticipants()}
					</section>
				)}

				{/* Ações */}
				<section className={clsx(classes.block, classes.blockFlush)}>
					<ButtonBase className={classes.actionRow} onClick={openEditContact}>
						<CreateOutlinedIcon />
						{i18n.t("contactDrawer.buttons.edit")}
					</ButtonBase>
					<ButtonBase
						className={clsx(classes.actionRow, !contact.active ? classes.actionPositive : classes.actionDanger)}
						onClick={() => contact.active
							? handleBlockContact(contact.id)
							: handleUnBlockContact(contact.id)}
						disabled={loading}
					>
						{!contact.active ? <LockOpenIcon /> : <BlockIcon />}
						{!contact.active
							? `${i18n.t("contactDrawer.buttons.unblock")} ${contact.name || ""}`
							: `${i18n.t("contactDrawer.buttons.block")} ${contact.name || ""}`}
					</ButtonBase>
				</section>
			</div>
		</>
	);

	const renderSearch = () => (
		<>
			{renderHeader("Pesquisar mensagens", () => setView("main"))}
			<div className={classes.subBody}>
				<div className={classes.searchBar}>
					<InputBase
						className={classes.searchField}
						placeholder="Pesquisar nas mensagens..."
						value={searchTerm}
						onChange={handleSearchChange}
						autoFocus
						inputProps={{ "aria-label": "Pesquisar nas mensagens" }}
						startAdornment={<SearchIcon />}
						endAdornment={searchTerm ? (
							<IconButton size="small" onClick={clearSearch} aria-label="Limpar pesquisa">
								<ClearIcon fontSize="small" />
							</IconButton>
						) : null}
					/>
				</div>
				<div className={classes.subScroll}>
					{renderSearchResults()}
				</div>
			</div>
		</>
	);

	const mediaTabs = [
		["Imagens", mediaData.images.length],
		["Vídeos", mediaData.videos.length],
		["Áudios", mediaData.audios.length],
		["Docs", mediaData.documents.length],
		["Links", mediaData.links.length],
	];

	const renderMedia = () => (
		<>
			{renderHeader("Mídia, links e docs", () => setView("main"))}
			<div className={classes.subBody}>
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					variant="scrollable"
					scrollButtons="auto"
					className={classes.tabs}
				>
					{mediaTabs.map(([label, count]) => (
						<Tab
							key={label}
							className={classes.tab}
							label={count > 0 ? `${label} ${count}` : label}
						/>
					))}
				</Tabs>
				<div className={classes.subScroll} role="tabpanel">
					{renderMediaTab()}
				</div>
			</div>
		</>
	);

	const renderErp = () => (
		<>
			{renderHeader("ERP", () => setView("main"))}
			<div className={classes.subBody}>
				<div className={clsx(classes.subScroll, classes.erpBody)}>
					<ContactErpPanel contact={contact} ticket={ticket} />
				</div>
			</div>
		</>
	);

	if (loading) return null;

	return (
		<>
			{newTicketModalOpen && (
				<NewTicketModal
					modalOpen={newTicketModalOpen}
					onClose={handleTicketCreated}
					initialContact={participantInitialContact || undefined}
				/>
			)}
			<ShowTicketOpenModal
				isOpen={openTicketAlert}
				handleClose={() => setOpenTicketAlert(false)}
				user={openTicketInfo.user}
				queue={openTicketInfo.queue}
			/>
			<ContactModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				contactId={contact.id}
			></ContactModal>
			<Drawer
				className={classes.drawer}
				variant={isMobile ? "temporary" : "persistent"}
				anchor="right"
				open={open}
				onClose={isMobile ? handleDrawerClose : undefined}
				PaperProps={{
					style: isMobile ? {
						width: "100vw",
						maxWidth: "400px",
						height: "100vh",
						position: "fixed",
						top: 0,
						right: 0,
						zIndex: 9999
					} : {
						position: "absolute",
						right: 0,
						top: 0,
						height: "100%",
						width: drawerWidth,
						zIndex: 1200
					}
				}}
				ModalProps={isMobile ? {
					style: { position: "fixed", zIndex: 9999 }
				} : {
					container: document.getElementById("drawer-container"),
					style: { position: "relative", zIndex: 1200 },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				{view === "search" && renderSearch()}
				{view === "media" && renderMedia()}
				{view === "erp" && renderErp()}
				{view === "main" && renderMain()}
			</Drawer>

			{/* Foto do contato ampliada */}
			<Dialog
				open={imageModalOpen}
				onClose={handleImageModalClose}
				classes={{ paper: classes.imageDialogPaper }}
				aria-label="Foto do contato"
			>
				<img
					src={contact?.urlPicture}
					alt={contact?.name || "Foto do contato"}
					className={classes.expandedImage}
					onClick={handleImageModalClose}
				/>
			</Dialog>
		</>
	);
};

export default ContactDrawer;
